import { useCallback, useEffect, useRef, useState } from "react";
import { DOTS_PATTERN } from "../constants";
import { useAiImageStoreShallow } from "../store";

interface AiImageGenerateProgressProps {
  /**
   * Duration in milliseconds for the progress to reach 95%
   * @default 30000 (30 seconds)
   */
  duration?: number;
  /**
   * Whether to automatically start the progress
   * @default true
   */
  autoStart?: boolean;
}

/**
 * Progress indicator component for AI image generation.
 * Shows a fake non-linear progress bar that stops at 95%.
 * The progress uses easing to feel realistic.
 */
export const AiImageGenerateProgress = ({
  duration = 30000,
  autoStart = true,
}: AiImageGenerateProgressProps) => {
  const type = useAiImageStoreShallow((s) => s.type);
  const [progress, setProgress] = useState(0.01); // Start at 1% so it's visible immediately
  const startTimeRef = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentProgressRef = useRef(0.01);

  // Non-linear easing function (ease-out cubic) to make progress feel realistic
  const easeOutCubic = useCallback((t: number): number => {
    return 1 - (1 - t) ** 3;
  }, []);

  // Calculate interval time that varies throughout the progress
  // Uses a combination of sine waves to create varying speeds (fast and slow)
  const getIntervalTime = useCallback((normalizedTime: number): number => {
    // Use multiple sine waves with different frequencies to create organic variation
    // This creates periods of fast (short intervals) and slow (long intervals) throughout
    const wave1 = Math.sin(normalizedTime * Math.PI * 4) * 0.5 + 0.5; // 4 cycles
    const wave2 = Math.sin(normalizedTime * Math.PI * 7) * 0.3 + 0.3; // 7 cycles
    const wave3 = Math.sin(normalizedTime * Math.PI * 11) * 0.2 + 0.2; // 11 cycles

    // Combine waves for organic variation
    const variation = wave1 + wave2 + wave3;

    // Map variation (0-1) to interval range (30ms to 300ms)
    // Lower variation = faster (shorter interval), higher variation = slower (longer interval)
    const minInterval = 30;
    const maxInterval = 300;
    const interval = minInterval + variation * (maxInterval - minInterval);

    return Math.max(minInterval, Math.min(maxInterval, interval));
  }, []);

  useEffect(() => {
    if (!autoStart) return;

    // Initialize start time
    startTimeRef.current = Date.now();
    currentProgressRef.current = 0.01;

    const updateProgress = () => {
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
        return;
      }

      const elapsed = Date.now() - startTimeRef.current;
      const normalizedTime = Math.min(elapsed / duration, 1);

      // Apply easing to make it feel more realistic
      const easedProgress = easeOutCubic(normalizedTime);

      // Stop at 95%
      const finalProgress = Math.min(easedProgress * 0.95, 0.95);
      currentProgressRef.current = finalProgress;
      setProgress(finalProgress);

      if (finalProgress >= 0.95) return;

      // Calculate next interval with varying speed (fast and slow throughout)
      const nextInterval = getIntervalTime(normalizedTime);
      timeoutRef.current = setTimeout(updateProgress, nextInterval);
    };

    // Start with a short initial delay
    timeoutRef.current = setTimeout(updateProgress, 50);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [autoStart, duration, easeOutCubic, getIntervalTime]);

  return (
    <div
      className="tw-flex tw-h-[376px] tw-justify-center tw-items-center tw-rounded-2xl tw-bg-blue-50 tw-px-6 tw-py-8"
      style={{
        backgroundImage: `url("${DOTS_PATTERN}")`,
        backgroundSize: "15px 15px",
      }}
    >
      <div className="tw-flex tw-flex-col tw-items-center tw-max-w-64 tw-w-full">
        <p className="tw-text-sm tw-font-medium tw-text-blue-700 tw-mt-0 tw-mb-2">
          Generating {type}...
        </p>
        <div className="tw-relative tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-3xl tw-bg-blue/10">
          <div
            className="tw-h-full tw-rounded-3xl tw-bg-primary"
            style={{
              width: `${progress * 100}%`,
              transition: "width 0.2s linear",
            }}
          />
        </div>
      </div>
    </div>
  );
};
